import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useResizeObserver } from "@/utils/useResizeObserver";

import type { ComponentProps } from "react";

type PathType = "stroke" | "fill";
/**
 * [pathType, pathString, pathWidth?]
 */
export type Path = [PathType, string, number?];

type Props = {
  paths: Path[];
} & ComponentProps<"svg">;

export function SvgGenerator({ paths, ...props }: Props) {
  const [size, setSize] = useState([0, 0]);
  const ref = useRef<SVGSVGElement>(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    setSize([ref.current.clientWidth, ref.current.clientHeight]);
  }, []);

  useResizeObserver(ref, (entry) => {
    setSize([entry.contentRect.width, entry.contentRect.height]);
  });

  const [width, height] = size;

  const markup = useMemo(() => {
    return paths
      .map(([pathType, pathString, pathWidth]) => {
        const d = pathString
          .replace(/\{(-?[.\d]+)w((?:\+|-)[.\d]+)?\}/g, (_, rel, abs) =>
            String((Number(rel) / 100) * width + Number(abs || 0)),
          )
          .replace(/\{(-?[.\d]+)h((?:\+|-)[.\d]+)?\}/g, (_, rel, abs) =>
            String((Number(rel) / 100) * height + Number(abs || 0)),
          );

        if (pathType !== "stroke") return `<path d="${d}"/>`;

        const strokeWidth = pathWidth == null ? "" : ` stroke-width="${pathWidth}"`;
        return `<path d="${d}" stroke="currentColor" fill="none"${strokeWidth}/>`;
      })
      .join("");
  }, [width, height, paths]);

  return (
    <svg
      ref={ref}
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      preserveAspectRatio="none"
      strokeWidth="1.24"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}
